<script lang="ts">
  import type { PageData } from './$types';
  import { TwistGrid, TwistGridItem, TwistBox } from '@twist/core';
  import ApplyForm from './components/ApplyForm.svelte';
  import ApplyTab from './components/ApplyTab.svelte';

  import { GREENHOUSE_BOARD_ID } from '$lib/careers/greenhouse/configPublic';
  import CareersThemeProvider, { getBUTheme } from './components/CareersThemeProvider.svelte';
  import Hero from './components/Hero.svelte';
  import JobDetails from './components/JobDetails.svelte';
  import BlockBuLockup, { Align, Theme } from '$components/Logos/BlockBULockup.svelte';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import CareersLayout from '../components/Layouts/CareersLayout.svelte';
  import LoadingBlock from '$components/LoadingBlock/LoadingBlock.svelte';

  export let data: PageData;

  let showApplyForm = false;
  let isLoadingForm = false;
  let contentContainer: HTMLDivElement;

  async function onApplyButtonClicked() {
    window.scrollTo({ top: contentContainer.offsetTop });
    showApplyForm = true;
  }
</script>

<svelte:head>
  <script
    defer
    fetchpriority="high"
    src={`https://boards.greenhouse.io/embed/job_board/js?for=${GREENHOUSE_BOARD_ID}`}
  ></script>
</svelte:head>

<CareersLayout>
  <CareersThemeProvider theme={getBUTheme(data.job.bu, 'dark')}>
    <Hero job={data.job} on:apply-clicked={onApplyButtonClicked} />
    <!-- Page does not use same desktop padding value as <Container/> section due to messing with fluid sizing for Greenhouse iframe -->
    <div class="job-container" bind:this={contentContainer}>
      <TwistGrid>
        <TwistGridItem colSpan={{ min: 4, lg: 7 }} colStart={{ min: 1, lg: 5 }} rowStart={1}>
          <ApplyTab bind:showApplyForm />
        </TwistGridItem>
        {#if !showApplyForm}
          <TwistGridItem colSpan={{ min: 4, lg: 7 }} colStart={{ min: 1, lg: 5 }} rowStart={2}>
            <TwistBox marginBottom={toFluidVarInput(12, 45)}>
              <BlockBuLockup
                businessUnit={data.job.bu}
                theme={Theme.DARK_ON_LIGHT}
                alignment={Align.LEFT}
              />
            </TwistBox>
          </TwistGridItem>
        {/if}
        <TwistGridItem colSpan={{ min: 4, lg: 7 }} colStart={{ min: 1, lg: 5 }} rowStart={3}>
          {#if showApplyForm && isLoadingForm}
            <LoadingBlock />
          {/if}
        </TwistGridItem>
        <TwistGridItem colSpan={{ min: 4, lg: 7 }} colStart={{ min: 1, lg: 5 }} rowStart={4}>
          <div
            id="job-details-content"
            role="tabpanel"
            aria-labelledby="job-details"
            class:hidden={showApplyForm}
          >
            <JobDetails job={data.job} on:apply-clicked={onApplyButtonClicked} />
          </div>
        </TwistGridItem>
      </TwistGrid>
      <div
        id="apply-form-content"
        role="tabpanel"
        aria-labelledby="apply-form"
        class:hidden={!showApplyForm}
        class:opacityNone={isLoadingForm}
      >
        <ApplyForm job={data.job} {showApplyForm} bind:isLoadingForm />
      </div>
    </div>
  </CareersThemeProvider>
</CareersLayout>

<style lang="scss">
  @import 'src/scss/media';

  .job-container {
    width: 100%;
    max-width: $max-width-page;
    padding: 0 var(--twist-core-spacing-4);
    margin: 0 auto;
    box-sizing: border-box;
  }

  .hidden {
    display: none;
  }

  .opacityNone {
    opacity: 0;
  }
</style>
